import { Component, type ErrorInfo, type ReactNode } from 'react';

interface State {
  error: Error | null;
}

/**
 * Top-level error boundary.
 *
 * A rendering error in one component should not leave the visitor staring at a white page. This
 * shows a recoverable message and logs the detail to the console for diagnosis. In production the
 * hook below is where an error-reporting service would be wired in.
 */
export class ErrorBoundary extends Component<{ children: ReactNode }, State> {
  state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  componentDidCatch(error: Error, info: ErrorInfo): void {
    // eslint-disable-next-line no-console -- the console is the diagnostic channel available here
    console.error('Unhandled rendering error:', error, info.componentStack);
  }

  private handleReset = () => {
    this.setState({ error: null });
  };

  render() {
    const { error } = this.state;
    if (!error) return this.props.children;

    return (
      <div className="flex min-h-screen items-center justify-center bg-surface px-6">
        <div className="max-w-md text-center">
          <p className="font-mono text-sm font-bold text-primary">Something broke</p>
          <h1 className="mt-4 font-display text-2xl font-bold text-ink">This page ran into an error</h1>
          <p className="mt-3 text-sm leading-relaxed text-muted">
            The failure has been logged. Reloading usually clears it; if it keeps happening, please tell us what you were
            doing when it occurred.
          </p>
          <div className="mt-8 flex flex-wrap justify-center gap-3">
            <button
              type="button"
              onClick={() => window.location.reload()}
              className="rounded-md bg-primary px-5 py-2.5 text-sm font-semibold text-primary-contrast hover:bg-primary-hover"
            >
              Reload the page
            </button>
            <a
              href="/contact"
              className="rounded-md border border-line-strong px-5 py-2.5 text-sm font-semibold text-ink no-underline hover:border-primary hover:text-primary"
            >
              Report the problem
            </a>
            <button type="button" onClick={this.handleReset} className="px-3 py-2.5 text-sm font-medium text-muted underline">
              Try again without reloading
            </button>
          </div>
          {import.meta.env.DEV && (
            <pre className="mt-8 overflow-x-auto rounded-md bg-sunken p-4 text-left font-mono text-xs text-danger">
              {error.message}
            </pre>
          )}
        </div>
      </div>
    );
  }
}
